// src/utils/fontLoader.js
const loadedFontsByUrl = new Set();
const loadedFamilies = new Set();

function normalizeFormat(url, providedFormat) {
  if (providedFormat) {
    const fmt = String(providedFormat).toLowerCase();
    if (fmt === 'otf') return 'opentype';
    if (fmt === 'ttf') return 'truetype';
    return fmt;
  }
  if (!url) return '';
  if (url.endsWith('.woff2')) return 'woff2';
  if (url.endsWith('.woff')) return 'woff';
  if (url.endsWith('.otf')) return 'opentype';
  if (url.endsWith('.ttf')) return 'truetype';
  return '';
}

function pickUrlsFromFontObj(fontObj) {
  // Возвращаем массив { url, format } в порядке предпочтения (woff2 -> woff -> ttf -> otf)
  if (!fontObj) return [];
  const urls = [];

  // если есть files: { regular: '...', ... }
  if (fontObj.files && typeof fontObj.files === 'object') {
    // добавляем все entries, но будем сортировать по предпочтению
    Object.keys(fontObj.files).forEach(k => {
      const u = fontObj.files[k];
      if (u) urls.push({ url: u, format: normalizeFormat(u, fontObj.format) });
    });
  }

  // прямой url fallback
  if (fontObj.url) urls.push({ url: fontObj.url, format: normalizeFormat(fontObj.url, fontObj.format) });
  if (fontObj.menu) urls.push({ url: fontObj.menu, format: normalizeFormat(fontObj.menu, fontObj.format) });

  // убираем дубликаты по url
  const uniq = Array.from(new Map(urls.map(x => [x.url, x])).values());

  // сортируем по предпочтению расширений
  const order = ['woff2','woff','truetype','opentype',''];
  uniq.sort((a,b) => order.indexOf(a.format) - order.indexOf(b.format));

  return uniq;
}

/**
 * Загружает шрифт и добавляет в document.fonts.
 */
export async function loadFontOnce({ family, url, format, fontObj } = {}) {

  if (typeof window === 'undefined' || typeof document === 'undefined') return false; // guard for SSR

  if (!family && !fontObj) return false;
  const fam = (family || fontObj.family || '').trim();
  if (!fam) return false;

  // если передан fontObj, предпочитаем его (files/url)
  const sources = pickUrlsFromFontObj(fontObj || { url, format });
  if (!sources.length) return false;

  // если уже загрузили этот family либо этот url — считаем что всё ок
  // проверяем все urls: если любой из них загружен — можно считать загруженным
  if (loadedFamilies.has(fam)) return true;
  if (sources.some(s => loadedFontsByUrl.has(s.url))) {
    loadedFamilies.add(fam);
    return true;
  }

  // строим строку source для FontFace (несколько форматов через запятую)
  const srcForFontFace = sources.map(s => {
    const fmt = s.format || normalizeFormat(s.url);
    return fmt ? `url("${s.url}") format("${fmt}")` : `url("${s.url}")`;
  }).join(', ');

  try {
    // FontFace конструктор принимает CSS-like источник
    const ff = new FontFace(fam, srcForFontFace, { weight: '400', style: 'normal' });
    await ff.load();
    document.fonts.add(ff);

    // пометим все url как загруженные
    sources.forEach(s => loadedFontsByUrl.add(s.url));
    loadedFamilies.add(fam);
    return true;
  } catch (e) {
    // fallback: document.fonts.load
    try {
      if (document.fonts && typeof document.fonts.load === 'function') {
        const loadPromise = document.fonts.load(`1rem "${fam}"`);
        const timeout = new Promise(res => setTimeout(res, 4000));
        await Promise.race([loadPromise, timeout]);
        if (document.fonts.check(`1rem "${fam}"`)) {
          sources.forEach(s => loadedFontsByUrl.add(s.url));
          loadedFamilies.add(fam);
          return true;
        }
      }
    } catch (e2) {
      // noop
    }
  }

  // последний шанс — инжектим @font-face в <style>
  try {
    const style = document.createElement('style');
    const familyEsc = fam.replace(/"/g, '\\"');
    // формируем src со всеми вариантами
    const srcCss = sources.map(s => {
      const fmt = s.format || normalizeFormat(s.url);
      return fmt ? `url("${s.url}") format("${fmt}")` : `url("${s.url}")`;
    }).join(', ');

    style.textContent = `
@font-face {
  font-family: "${familyEsc}";
  src: ${srcCss};
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
`;
    document.head.appendChild(style);
    sources.forEach(s => loadedFontsByUrl.add(s.url));
    loadedFamilies.add(fam);
    return true;
  } catch (err) {
    console.warn('fontLoader: could not inject @font-face', err);
  }

  return false;
}

/**
 * Рекурсивно обходит items и загружает все шрифты.
 * items — массив элементов (item.settings.font)
 */
export async function loadFontsFromItems(items = []) {
  if (!Array.isArray(items)) return;
  const tasks = [];

  const seen = new Set();

  function walk(arr) {
    if (!Array.isArray(arr)) return;
    arr.forEach(it => {
      if (!it || seen.has(it)) return;
      seen.add(it);

      const f = it?.settings?.font;
      if (f && (f.family && (f.url || (f.files && Object.keys(f.files).length)))) {
        tasks.push(loadFontOnce({ fontObj: f }));
      }

      // рекурсивно обрабатывать разные поля, где могут лежать дети
      if (Array.isArray(it.data)) walk(it.data);
      if (it.children) {
        if (Array.isArray(it.children)) walk(it.children);
        else if (Array.isArray(it.children.data)) walk(it.children.data);
      }
      if (Array.isArray(it.elements)) walk(it.elements); // на всякий случай
    });
  }

  walk(items);
  await Promise.all(tasks);
}
